// Copyright (c) 2020 embyt GmbH. All rights reserved.

import { IOutput, OutputMode } from "shared/types/parameter";
import { IStateDataOutput } from "states/datastates";
import { isSet, roundToDigits } from "shared/utils/utils";

export enum IOState {
  OFF,
  ON,
  UNKNOWN,
}

export enum IoStateFlag {
  LINX_REQ_STATE = 1,
  OVERRIDE = 4,
  MANUAL = 16,
}

const DIGITS_TO_SHOW = 3;

class Output {
  public readonly parameter: IOutput;
  public readonly value: IStateDataOutput | undefined;
  public readonly state: IOState;
  public readonly isOffline: boolean | undefined;
  public readonly isOverride: boolean | undefined;

  constructor(
    parameter: IOutput,
    value: IStateDataOutput | undefined,
    moduleStatus: boolean | undefined,
  ) {
    this.parameter = parameter;
    this.value = value;
    this.isOffline = this.deriveOfflineStatus(moduleStatus);
    this.isOverride = this.deriveOverrideStatus();
    this.state = this.deriveState();
  }

  private deriveOfflineStatus(moduleStatus: boolean | undefined) {
    // first, check module status
    if (moduleStatus === undefined) {
      return undefined;
    }
    return !moduleStatus || this.value?.logical === undefined;
  }

  private deriveState() {
    // check output value
    // no value at all?
    if (this.value?.logical === undefined) {
      return IOState.UNKNOWN;
    }
    // we have at least a logical state available
    if (this.parameter.mode === OutputMode.ANALOG) {
      // state unknown for analog outputs
      return IOState.UNKNOWN;
    }
    // digital or PWM outputs
    const value =
      isSet(this.value.physical) && !isNaN(this.value.physical) ?
        this.value.physical
      : this.value.logical;
    // physical value sometimes gets to something like -5E-39...
    // also, physical state may be -1 in case of LIOBs
    const boolValue = Math.abs(value) > 1e-10;
    const invert = this.parameter.mode === OutputMode.OPEN;
    return boolValue !== invert ? IOState.ON : IOState.OFF;
  }

  private deriveOverrideStatus() {
    if (![OutputMode.ANALOG, OutputMode.PWM].includes(this.parameter.mode)) {
      // override flag
      return isSet(this.value?.state) && (this.value!.state & IoStateFlag.OVERRIDE) > 0;
    } else if (this.parameter.mode === OutputMode.PWM) {
      // no override info available for PWM outputs
      return undefined;
    }
    // analog outputs do not have override
    return false; // default
  }

  public getText(): string {
    if (this.value?.logical === undefined) {
      return "offline";
    }
    const value = `${roundToDigits(this.value.logical, DIGITS_TO_SHOW)}`;
    return isSet(this.parameter.unit) ? `${value} ${this.parameter.unit}` : value;
  }
}

export default Output;
